import { type ConvertCamelToSnakeCase } from "shared/types";
import { type FeatureKey } from "shared/types/features";
import {
  type MentionType,
  type SocialMentionType,
} from "shared/types/mentions";

export type MediumName =
  | "Influencer Social"
  | "Magazine"
  | "Online"
  | "Podcast"
  | "Print"
  | "Radio"
  | "Social"
  | "TV";

export interface RelatedType {
  appLabel: string;
  checked: boolean;
  color?: string;
  featureFlag: FeatureKey;
  featureId?: number;
  field: MentionType;
  hidden?: boolean;
  icon?: string;
  label: string;
}

export enum SocialPlatform {
  BLOG = "blog",
  BLUESKY = "bluesky",
  FACEBOOK = "facebook",
  FORUM = "forum",
  INSTAGRAM = "instagram",
  LINKEDIN = "linkedin",
  REDDIT = "reddit",
  TIKTOK = "tiktok",
  TWITTER = "twitter",
  YOUTUBE = "youtube",
}

export enum MediumField {
  MAGAZINE = "magazine",
  ONLINE = "online",
  PODCAST = "podcast",
  PRINT = "print",
  RADIO = "radio",
  SOCIAL = "social",
  TV = "tv",
}

// One flag per medium — `online_content`, `print_content`, and so on. Derived
// from `MediumField` so adding a medium can't leave a consumer behind.
export type MediumContentFlags = {
  [Field in MediumField as `${Field}_content`]?: boolean;
};

export interface MediumPlatform {
  checked: boolean;
  color: string;
  featureFlag: FeatureKey;
  fields: SocialMentionType[];
  icon: string;
  label: string;
  platform: SocialPlatform;
  widgets?: string[];
}

export interface BaseMedium {
  categoryLocationFiltersEnabled: boolean;
  classField: string;
  color: string;
  createActivity?: boolean;
  displayExcerptsFeature: string;
  featureAllowanceFlag?: FeatureKey;
  featureFlag: FeatureKey;
  featureName?: string;
  field: MediumField;
  filterEnabled: boolean;
  hasNotification: boolean;
  icon: string;
  label: string;
  medium: MediumName;
  relatedCamelCaseField?: string;
  relatedCamelCaseFields: string[];
  relatedFields: string[];
  relatedLabel?: string;
  relatedLabels: string[];
  relatedTypes: RelatedType[];
  resource: string;
  sourceLabel: string;
  triggerAlert: boolean;
  viewFeatureFlag?: FeatureKey;
  viewPopupAllowOverspend?: boolean;
  viewPopupShown?: string;
  viewWarningFeatureFlag?: FeatureKey;
}

export interface OnlineMedium extends BaseMedium {
  field: MediumField.ONLINE;
}

export interface PrintMedium extends BaseMedium {
  field: MediumField.PRINT;
}

export interface MagazineMedium extends BaseMedium {
  field: MediumField.MAGAZINE;
}

export interface PodcastMedium extends BaseMedium {
  field: MediumField.PODCAST;
}

export interface TvMedium extends BaseMedium {
  field: MediumField.TV;
}

export interface RadioMedium extends BaseMedium {
  field: MediumField.RADIO;
}

export interface SocialMedium extends Omit<
  BaseMedium,
  "resource" | "classField"
> {
  field: MediumField.SOCIAL;
  platforms: MediumPlatform[];
}

export interface InfluencerSocialMedium extends Omit<
  BaseMedium,
  "resource" | "classField"
> {
  field: MediumField.SOCIAL;
}

export type ExternalSource = Omit<
  BaseMedium,
  "medium" | "field" | "displayExcerptsFeature"
>;

export type Medium =
  | OnlineMedium
  | PrintMedium
  | MagazineMedium
  | PodcastMedium
  | TvMedium
  | RadioMedium
  | SocialMedium
  | InfluencerSocialMedium;

export type MediumByClass = Exclude<
  Medium,
  InfluencerSocialMedium | SocialMedium
>;

export type MediumContentKey =
  | "magazineContent"
  | "onlineContent"
  | "podcastContent"
  | "printContent"
  | "radioContent"
  | "socialContent"
  | "tvContent";

export const mediumFieldToContentKey: Record<MediumField, MediumContentKey> = {
  [MediumField.MAGAZINE]: "magazineContent",
  [MediumField.ONLINE]: "onlineContent",
  [MediumField.PODCAST]: "podcastContent",
  [MediumField.PRINT]: "printContent",
  [MediumField.RADIO]: "radioContent",
  [MediumField.SOCIAL]: "socialContent",
  [MediumField.TV]: "tvContent",
};

export type MediumSnakeContentKey = ConvertCamelToSnakeCase<MediumContentKey>;

export const mediumFieldToSnakeKey: Record<MediumField, MediumSnakeContentKey> =
  {
    [MediumField.MAGAZINE]: "magazine_content",
    [MediumField.ONLINE]: "online_content",
    [MediumField.PODCAST]: "podcast_content",
    [MediumField.PRINT]: "print_content",
    [MediumField.RADIO]: "radio_content",
    [MediumField.SOCIAL]: "social_content",
    [MediumField.TV]: "tv_content",
  };
